import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { rideAPI, riderAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import { MapPin, CalendarIcon, Clock, DollarSign, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, addHours, addDays, isBefore, isAfter, startOfDay } from 'date-fns';

const ScheduleRide = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [pickup, setPickup] = useState({ lat: null, lng: null, address: '' });
  const [dropoff, setDropoff] = useState({ lat: null, lng: null, address: '' });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');

  const minDate = addHours(new Date(), 2);
  const maxDate = addDays(new Date(), 14);

  // Generate time slots
  const timeSlots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      const timeStr = `${hour}:${minute}`;
      const displayStr = format(new Date(2000, 0, 1, h, m), 'h:mm a');
      timeSlots.push({ value: timeStr, label: displayStr });
    }
  }

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        // Just store for reference, don't auto-fill
      });
    }
  }, []);

  const getEstimate = async () => {
    if (!pickup.lat || !dropoff.lat) return;

    setEstimating(true);
    try {
      const response = await rideAPI.getEstimate({
        pickup_location: pickup,
        dropoff_location: dropoff,
      });
      setEstimate(response.data);
    } catch (error) {
      console.error('Error getting estimate:', error);
    } finally {
      setEstimating(false);
    }
  };

  useEffect(() => {
    if (pickup.lat && dropoff.lat) {
      getEstimate();
    }
  }, [pickup.lat, dropoff.lat]);

  const handlePickupSelect = (location) => {
    setPickup(location);
  };

  const handleDropoffSelect = (location) => {
    setDropoff(location);
  };

  const scheduleRide = async () => {
    if (!estimate || !selectedDate || !selectedTime) {
      toast.error('Please select pickup, dropoff, date and time');
      return;
    }

    const [hours, minutes] = selectedTime.split(':');
    const scheduledDateTime = new Date(selectedDate);
    scheduledDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    // Validate time
    const minAllowedTime = addHours(new Date(), 2);
    if (isBefore(scheduledDateTime, minAllowedTime)) {
      toast.error('Scheduled time must be at least 2 hours from now');
      return;
    }

    if (isAfter(scheduledDateTime, maxDate)) {
      toast.error('Scheduled time must be within 14 days');
      return;
    }

    setLoading(true);
    try {
      const response = await rideAPI.requestRide({
        pickup_location: pickup,
        dropoff_location: dropoff,
        scheduled_time: scheduledDateTime.toISOString(),
      });
      
      toast.success('Ride scheduled successfully!');
      navigate(`/rider/ride/${response.data.ride.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule ride');
    } finally {
      setLoading(false);
    }
  };

  const isDateDisabled = (date) => {
    const today = startOfDay(new Date());
    const maxAllowed = startOfDay(addDays(new Date(), 14));
    return isBefore(date, today) || isAfter(date, maxAllowed);
  };

  return (
    <div className="p-6" data-testid="schedule-ride-page">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Schedule a Ride
        </h1>

        <div className="space-y-6">
          {/* Location Inputs */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    Pickup Location
                  </Label>
                  <AddressAutocomplete
                    value={pickup.address}
                    onChange={(val) => setPickup(prev => ({ ...prev, address: val }))}
                    onSelect={handlePickupSelect}
                    placeholder="Enter pickup location"
                    icon={MapPin}
                    iconColor="text-green-500"
                    data-testid="schedule-pickup-input"
                  />
                </div>

                <div className="flex items-center gap-3 py-2">
                  <div className="w-5 flex flex-col items-center">
                    <div className="w-0.5 h-8 bg-slate-200 dark:bg-slate-600"></div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    Dropoff Location
                  </Label>
                  <AddressAutocomplete
                    value={dropoff.address}
                    onChange={(val) => setDropoff(prev => ({ ...prev, address: val }))}
                    onSelect={handleDropoffSelect}
                    placeholder="Where to?"
                    icon={MapPin}
                    iconColor="text-red-500"
                    data-testid="schedule-dropoff-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Date & Time Selection */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-red-600" />
                Select Date & Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Date Picker */}
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start h-12 font-normal"
                        data-testid="date-picker-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={isDateDisabled}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Time Picker */}
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Select value={selectedTime} onValueChange={setSelectedTime}>
                    <SelectTrigger className="h-12" data-testid="time-picker">
                      <SelectValue placeholder="Select time">
                        {selectedTime ? timeSlots.find(t => t.value === selectedTime)?.label : 'Select time'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot.value} value={slot.value}>
                          {slot.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <p className="text-sm text-slate-500 dark:text-slate-400 mt-4">
                <Clock className="w-4 h-4 inline mr-1" />
                Schedule 2 hours to 14 days in advance. Free cancellation up to 1 hour before if no driver is confirmed.
              </p>
            </CardContent>
          </Card>

          {/* Fare Estimate */}
          {estimate && (
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-red-600" />
                  Estimated Fare
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {estimate.distance_miles} miles · ~{estimate.duration_minutes} min
                    </p>
                  </div>
                  <span className="text-3xl font-display font-bold text-slate-900 dark:text-white">
                    {formatCurrency(estimate.subtotal)}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Schedule Button */}
          <Button
            onClick={scheduleRide}
            disabled={loading || !estimate || !selectedDate || !selectedTime}
            className="w-full h-14 rounded-full bg-red-600 hover:bg-red-700 font-bold uppercase tracking-wider text-lg shadow-lg shadow-red-600/20"
            data-testid="schedule-ride-btn"
          >
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>Schedule Ride</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleRide;
